// User types
export interface User {
  email: string;
  isAdmin: boolean;
  isVerified?: boolean;
  subscriberId?: string;
  entityId?: string;
}

// Authentication types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  login: string;
  password: string;
  confirmPassword: string;
}

export interface AuthResponse {
  token: string;
  email: string;
  login: string;
  isAdmin: boolean;
  isVerified?: boolean;
  message: string;
}

// Dashboard types
export interface DashboardData {
  message: string;
  type: 'subscriber' | 'admin';
  timestamp: number;
}

// API Response types
export interface ApiResponse {
  message: string;
}

// Backend response types (Map<String, String> equivalent)
export interface BackendResponse {
  message: string;
}

// Form types
export interface FormData {
  [key: string]: string;
}

// Component props types
export interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export interface GoogleAuthProps {
  onSuccess?: (response: AuthResponse) => void;
  onError?: (errorMessage: string) => void;
}

// Context types
export interface AuthContextType {
  token: string | null;
  user: User | null;
  login: (token: string) => void;
  logout: () => void;
  loading: boolean;
}

// Error types
export interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

// Client Types
export interface Client {
  id: string;
  companyId: string;
  clientNumber?: number;
  ice?: string;
  name: string;
  number?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  creationDate?: string;
  modificationDate?: string;
  active: boolean;
}

export interface ClientRequest {
  companyId: string;
  clientNumber?: number;
  ice?: string;
  name: string;
  number?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
}

// Supplier Types
export interface Supplier {
  id: string;
  companyId: string;
  supplierNumber: number;
  ice?: string;
  name: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  creationDate?: string;
  modificationDate?: string;
  active: boolean;
}

export interface SupplierRequest {
  companyId: string;
  supplierNumber?: number;
  ice?: string;
  name: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
}

// Company Types
export interface Company {
  id: string;
  creationDate?: string;
  modificationDate?: string;
  ice?: string;
  cnss?: string;
  rc?: string;
  fi?: string;
  tradLicense?: string;
  capital?: number;
  name: string;
  vatRate?: number;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  rib?: string;
  logo?: string;
  mailUsername?: string;
  mailPassword?: string;
  mailSubject?: string;
  mailBody?: string;
  lang?: string;
  templateId?: string; // ID of the selected template for this company
  startingOffset?: number; // Starting offset for invoice numbering
  clientStartingOffset?: number; // Starting offset for client numbering
  supplierStartingOffset?: number; // Starting offset for supplier numbering
  encryptionFeatureEnabled?: boolean;
  currency?: string; // Currency code, e.g. 'MAD', 'EUR', 'USD'
  // Cash threshold feature
  cashThresholdEnabled?: boolean;
  dailyCashThreshold?: number;
  monthlyCashThreshold?: number;
  active: boolean;
}

export interface CompanyRequest {
  ice?: string;
  cnss?: string;
  rc?: string;
  fi?: string;
  tradLicense?: string;
  capital?: number;
  name: string;
  vatRate?: number;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  rib?: string;
  logo?: File | null;
  mailUsername?: string;
  mailPassword?: string;
  mailSubject?: string;
  mailBody?: string;
  lang?: string;
  templateId?: string; // ID of the selected template for this company
  startingOffset?: number; // Starting offset for invoice numbering
  clientStartingOffset?: number; // Starting offset for client numbering
  supplierStartingOffset?: number; // Starting offset for supplier numbering
  encryptionFeatureEnabled?: boolean;
  currency?: string; // Currency code, e.g. 'MAD', 'EUR', 'USD'
  // Cash threshold feature
  cashThresholdEnabled?: boolean;
  dailyCashThreshold?: number;
  monthlyCashThreshold?: number;
  deleteLogo?: boolean; // Flag to explicitly remove the logo
}

export interface CompanySelection {
  selectedCompanyId: string | null;
  selectedCompany: Company | null;
}

// Cash threshold totals for validation
export interface CashTotals {
  dailyTotal: number;
  monthlyTotal: number;
}

// Category Types
export interface Category {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  active: boolean;
}

// Product Types
export type ProductStatus = 'ACTIVE' | 'INACTIVE' | 'OUT_OF_STOCK' | 'DISCONTINUED';

export interface Product {
  id: string;
  companyId: string;
  categoryId?: string;
  name: string;
  code: string;
  unitPrice: number;
  unity?: string;
  purchasePrice: number;
  quantityOnHand: number;
  reservedQuantity: number;
  status: ProductStatus;
  active: boolean;
}

export interface OrderItem {
  id?: string;
  companyId: string;
  productId?: string;
  code: string;
  description: string;
  unitPrice: number;
  unity?: string;
  quantity: number;
}

// Order Types
export type OrderStatus = 'RESERVATION' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
export type OrderType = 'SALE' | 'PURCHASE';

export interface Payment {
  id: string;
  amount: number;
  date: string;
  description?: string;
  method: PaymentMethod;
}

export interface AddPaymentRequest {
  invoiceId: string;
  amount: number;
  description?: string;
  method: PaymentMethod;
}

export interface Order {
  id: string;
  companyId: string;
  clientId?: string;
  supplierId?: string;
  invoiceId?: string;
  status: OrderStatus;
  orderType: OrderType;
  items: OrderItem[];
  paymentHistory: Payment[];
  totalAmount: number;
  vatRate: number;
  discount: number;
  discountAmount?: number;
  totalHT: number;
  totalHtAfterDiscount?: number;
  creationDate: string;
}

// Stock Movement Types
export type StockMovementType = 'IN' | 'OUT' | 'RESERVED' | 'RELEASED' | 'ADJUSTMENT';

export interface StockMovement {
  id: string;
  companyId: string;
  productId: string;
  movementType: StockMovementType;
  quantity: number;
  orderId?: string;
  createdAt: string;
}

// Invoice Types
export interface OrderRequest {
  items?: OrderItem[];
  vatRate: number;
  discount?: number;
  discountAmount?: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: number;
  formattedInvoiceNumber: string;
  // companyLogo removed
  issuerId: string;
  clientId: string;

  originalQuoteId: string | undefined;
  convertedToInvoiceId: string | undefined;

  // Issuer (Company) Information
  issuerName: string;
  issuerIce: string;
  issuerCnss: string;
  issuerRc: string;
  issuerFi: string;
  issuerTradLicense?: string;
  issuerCapital: number;
  issuerStreet?: string;
  issuerCity: string;
  issuerPostalCode?: string;
  issuerCountry: string;
  issuerWebsite?: string;
  issuerRib?: string;
  issuerPhone?: string;
  issuerEmail?: string;

  // Client Information
  clientName: string;
  clientIce: string;
  clientNumber?: number;
  clientStreet?: string;
  clientCity: string;
  clientPostalCode?: string;
  clientCountry: string;

  // Invoice Details
  currency: string;
  invoiceType: string;
  paymentMethod: string;
  creationDate: string;
  modificationDate: string;
  totalInWords?: string;
  active: boolean;

  // Order Details
  order?: Order;

  generationId?: string;
  language?: string;
  deadlineDate?: string;
}

export interface InvoiceRequest {
  issuerId: string;
  clientId: string;
  orderId?: string;
  invoiceNumber?: number;
  creationDate?: string; // ISO date string (YYYY-MM-DD)
  deadlineDate?: string; // ISO date string (YYYY-MM-DD)
  currency: string;
  invoiceType: string;
  paymentMethod: string;
  totalInWords?: string;
  language?: string;
  order: OrderRequest;
}

// Enum Types for better type safety
export enum PaymentMethod {
  CASH = 'CASH',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CREDIT_CARD = 'CREDIT_CARD',
  PAYPAL = 'PAYPAL',
  MOBILE_PAYMENT = 'MOBILE_PAYMENT',
  CHEQUE = 'CHEQUE',
  EFFECTS = 'EFFECTS',
}

export enum InvoiceType {
  SELL_INVOICE = 'SELL_INVOICE',
  PURCHASE_INVOICE = 'PURCHASE_INVOICE',
  QUOTE = 'QUOTE',
}

export enum Currency {
  MAD = 'MAD',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  CAD = 'CAD',
  AUD = 'AUD',
  CHF = 'CHF',
  JPY = 'JPY',
  CNY = 'CNY',
}

// Pagination response type
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface EntityConfig {
  id: string;
  name: string;
  entityId: string;
  maxCompanies: number;
  stockFeatureEnabled?: boolean;
  packageId?: string;
  customTemplatesEnabled?: boolean;
  encryptionEnabled?: boolean;
  settings?: Record<string, any>;
  creationDate: string;
  modificationDate: string;
  subscriptionEndDate?: string;
}

export interface Subscriber {
  id: string;
  email: string;
  login: string;
  active: boolean;
  entityConfigId?: string;
  creationDate?: string;
  modificationDate?: string;
}

export enum TemplateType {
  WYSIWYG = 'WYSIWYG',
  STATIC = 'STATIC',
}

export interface InvoiceTemplate {
  id: string;
  entityId: string;
  name: string;
  inlinedHtmlAndCss: string;
  type: TemplateType;
}


export interface AppConfig {
  id: string;
  registrationEnabled: boolean;
  defaultEntityConfig?: EntityConfig;
  freeDays: number;
}

// EDI Export Types
export type EDIExportType = 'FEC' | 'TIERS' | 'TVA' | 'STATS';

export interface EDIExportRequest {
  companyIds: string[];
  startDate: string;
  endDate: string;
  exportTypes: EDIExportType[];
}
export interface CreateAgentRequest {
  email: string;
  login: string;
  password: string;
  allowedCompanyIds: string[];
  canCreateClients: boolean;
  canCreateCompanies: boolean;
  canCreateInvoices: boolean;
  canEditClients: boolean;
  canEditCompanies: boolean;
  canEditInvoices: boolean;
  canDeleteClients: boolean;
  canDeleteCompanies: boolean;
  canDeleteInvoices: boolean;
  canCreateOrders: boolean;
  canEditOrders: boolean;
  canDeleteOrders: boolean;
  canCreateSuppliers: boolean;
  canEditSuppliers: boolean;
  canDeleteSuppliers: boolean;
  canViewAllCompanies: boolean;
}

export interface Agent {
  id: string;
  email: string;
  login: string;
  active: boolean;
  emailVerified: boolean;
  canCreateClients: boolean;
  canCreateCompanies: boolean;
  canCreateInvoices: boolean;
  canEditClients: boolean;
  canEditCompanies: boolean;
  canEditInvoices: boolean;
  canDeleteClients: boolean;
  canDeleteCompanies: boolean;
  canDeleteInvoices: boolean;
  canCreateOrders: boolean;
  canEditOrders: boolean;
  canDeleteOrders: boolean;
  canCreateSuppliers: boolean;
  canEditSuppliers: boolean;
  canDeleteSuppliers: boolean;
  canViewAllCompanies: boolean;
  allowedCompanyIds: string[];
  creationDate: string;
}


export interface InvoiceSearchQuery {
  companyId: string;
  startDate?: string;
  endDate?: string;
  startDeadlineDate?: string;
  endDeadlineDate?: string;
  invoiceNumber?: number;
  clientId?: string;
  clientName?: string;
  invoiceType?: string;
  currency?: string;
  paymentMethod?: string;
  page: number;
  size: number;
}

export interface InvoiceSearchResponse {
  content: Invoice[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  last: boolean;
}